import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardBody,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";

export interface InvoiceSoaSummaryRow {
  id: number;
  dli_sa_number: string;
  work_order: string;
  total_amount: number;
}

const formatCurrency = (value: number): string =>
  `P ${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function normalizeSoaIds(ids: number[]): number[] {
  return Array.from(
    new Set(
      ids
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  );
}

export interface InvoiceSoaSummaryTableProps {
  soaIds: number[];
}

/**
 * Fetches SOAs via POST /soa/by-ids and shows DLI-SA Number / Work Order /
 * Total Amount with a Total Gross Amount footer row (mirrors SOA line-items totals UI).
 */
export function InvoiceSoaSummaryTable({ soaIds }: InvoiceSoaSummaryTableProps) {
  const colors = useThemeColors();
  const { showError } = useToastNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<InvoiceSoaSummaryRow[]>([]);
  const [totalGross, setTotalGross] = useState<number | null>(null);

  const soaIdsKey = normalizeSoaIds(soaIds).join(",");
  const normalizedIds = soaIdsKey
    ? soaIdsKey.split(",").map((id) => Number(id))
    : [];

  useEffect(() => {
    let cancelled = false;

    if (normalizedIds.length === 0) {
      setRows([]);
      setTotalGross(null);
      return () => {
        cancelled = true;
      };
    }

    const fetchSoas = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.post("/soa/by-ids", {
          statement_of_account_ids: normalizedIds,
        });
        if (cancelled) return;

        const list = response.data?.soa ?? [];
        const gross = response.data?.total_gross;

        setRows(
          Array.isArray(list)
            ? list.map((item: Record<string, unknown>) => {
                const workOrderRaw = item.work_order;
                const workOrder =
                  workOrderRaw == null || String(workOrderRaw).trim() === ""
                    ? ""
                    : String(workOrderRaw).trim();
                return {
                  id: Number(item.id),
                  dli_sa_number: String(item.dli_sa_number ?? ""),
                  work_order: workOrder,
                  total_amount: Number(item.total_amount ?? 0),
                };
              })
            : [],
        );
        setTotalGross(
          gross == null || Number.isNaN(Number(gross)) ? null : Number(gross),
        );
      } catch (error: unknown) {
        if (!cancelled) {
          showError(error as Error);
          setRows([]);
          setTotalGross(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchSoas();
    return () => {
      cancelled = true;
    };
    // Intentionally depend on soaIdsKey (stable string), not normalizedIds array ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soaIdsKey, showError]);

  if (normalizedIds.length === 0) {
    return null;
  }

  return (
    <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border} minW={0}>
      <CardBody p={0} minW={0}>
        {isLoading ? (
          <Box p={8} textAlign="center">
            <Spinner size="lg" color="blue.500" />
            <Text mt={4} color={colors.text}>
              Loading SOA details...
            </Text>
          </Box>
        ) : rows.length === 0 ? (
          <Box p={8} textAlign="center">
            <Text color={colors.textSecondary}>No SOA records found</Text>
          </Box>
        ) : (
          <>
            <Box minW={0} overflowX="auto">
              <TableContainer minW="min-content">
                <Table variant="simple" size="sm">
                  <Thead bg={colors.sectionBg}>
                    <Tr>
                      <Th borderColor={colors.border} color={colors.text}>
                        DLI-SA Number
                      </Th>
                      <Th borderColor={colors.border} color={colors.text}>
                        Work Order Number
                      </Th>
                      <Th
                        borderColor={colors.border}
                        color={colors.text}
                        isNumeric
                      >
                        Total Amount
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((row) => (
                      <Tr key={row.id}>
                        <Td borderColor={colors.border} color={colors.text}>
                          {row.dli_sa_number || "-"}
                        </Td>
                        <Td borderColor={colors.border} color={colors.text}>
                          {row.work_order || "-"}
                        </Td>
                        <Td
                          borderColor={colors.border}
                          color={colors.text}
                          isNumeric
                        >
                          {formatCurrency(row.total_amount)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                  {totalGross != null && (
                    <Tfoot>
                      <Tr bg={colors.sectionBg} fontWeight="semibold">
                        <Td borderColor={colors.border} color={colors.text}>
                          Total Gross Amount
                        </Td>
                        <Td borderColor={colors.border} color={colors.text} />
                        <Td
                          borderColor={colors.border}
                          color={colors.text}
                          isNumeric
                        >
                          {formatCurrency(totalGross)}
                        </Td>
                      </Tr>
                    </Tfoot>
                  )}
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </CardBody>
    </Card>
  );
}
